import * as THREE from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export type BoundaryEdge = [THREE.Vector3, THREE.Vector3];

export type BoundaryEdges = BoundaryEdge[];

/**
 * Finds boundary edges in an indexed geometry.
 * Returns an array of pairs of Vector3 positions [ [v0, v1], [v0, v1], ... ].
 */
export function getBoundaryEdges(geometry: THREE.BufferGeometry): BoundaryEdges {
  // Ensure geometry is indexed
  if (!geometry.index) {
    geometry = mergeVertices(geometry);
  }
  const index = geometry.index!;
  const posAttr = geometry.attributes.position;

  // Edge usage map: key is \"minIndex-maxIndex\", value is count of how many faces share that edge
  const edgeUsage = new Map();

  // Helper to record edges
  function recordEdge(i1: number, i2: number) {
    // Sort the indices so the edge key is consistent (minIndex-maxIndex)
    const a = Math.min(i1, i2);
    const b = Math.max(i1, i2);
    const key = `${a}-${b}`;
    edgeUsage.set(key, (edgeUsage.get(key) || 0) + 1);
  }

  // Traverse each face
  for (let f = 0; f < index.count; f += 3) {
    const i1 = index.getX(f);
    const i2 = index.getX(f + 1);
    const i3 = index.getX(f + 2);

    recordEdge(i1, i2);
    recordEdge(i2, i3);
    recordEdge(i3, i1);
  }

  // Now extract edges that appear exactly once
  const boundaryEdges: BoundaryEdges = [];
  edgeUsage.forEach((count, key) => {
    if (count === 1) {
      // This is a boundary edge
      const [a, b] = key.split('-').map(Number);

      // Convert these indices to actual 3D positions
      const vA = new THREE.Vector3(posAttr.getX(a), posAttr.getY(a), posAttr.getZ(a));
      const vB = new THREE.Vector3(posAttr.getX(b), posAttr.getY(b), posAttr.getZ(b));

      boundaryEdges.push([vA, vB]);
    }
  });

  return boundaryEdges;
}
